import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Lock, Mail, Pin, Visibility, VisibilityOff } from "@mui/icons-material";
import { confirmSignIn, resetPassword, signIn } from "aws-amplify/auth";

import { useAuth } from "../../app/providers/AuthProvider.jsx";
import AuthIntegrationStrip from "./components/AuthIntegrationStrip.jsx";
import AuthTextField from "./components/AuthTextField.jsx";
import logoLogin123Zap from "../../shared/assets/images/logo-login-123zap.png";
import { purple } from "../../shared/theme/themePrimitives";
import { GetUsernameByEmail } from "../../services/api/Auth/GetUsernameByEmail";
import { notify } from "../../shared/utils/utils";

const METHOD_LOGIN_KEY = "METHOD_LOGIN";
const signupUrl = "https://contato.123zap.com.br";

function normalizeLoginError(errorMessage) {
  const message = errorMessage?.trim();

  if (!message) {
    return "Ocorreu um erro ao tentar fazer login. Tente novamente.";
  }

  const lowerMessage = message.toLowerCase();

  if (
    lowerMessage.includes("invalid credential") ||
    lowerMessage.includes("user does not exist") ||
    lowerMessage.includes("incorrect username or password") ||
    lowerMessage.includes("not authorized")
  ) {
    return "Usuário ou senha inválidos.";
  }

  if (message.includes("Field") && message.includes("is required")) {
    return "Preencha todos os campos obrigatórios.";
  }

  const knownMessages = {
    "Invalid credentials": "Credenciais inválidas. Por favor tente novamente.",
    "User does not exist.": "Credenciais inválidas. Por favor tente novamente.",
    "You need to provide your password.": "Você precisa fornecer sua senha.",
    "Please provide the email OTP code sent to your email.":
      "Insira o código enviado para seu e-mail.",
    "Please provide the MFA code from your authenticator app.":
      "Insira o código MFA do seu aplicativo autenticador.",
    "Please provide a new password.": "Você precisa fornecer uma nova senha.",
    "An unexpected error occurred":
      "Ocorreu um erro inesperado. Tente novamente mais tarde.",
  };

  return knownMessages[message] || "Ocorreu um erro ao tentar fazer login. Tente novamente.";
}

function normalizeLoginStateMessage(stateMessage) {
  const message = String(stateMessage || "").trim();

  if (!message) return "";

  if (
    !/invalid|error|password|user|credential|required|unexpected|incorrect|authorized/i.test(message)
  ) {
    return message;
  }

  return normalizeLoginError(message);
}

function BrandPanel({ isMfa }) {
  return (
    <Box
      sx={{
        position: "relative",
        display: { xs: "none", md: "flex" },
        alignItems: "center",
        minHeight: "100dvh",
        overflow: "hidden",
        px: { md: 4, lg: 8, xl: 14 },
        py: { md: 5, lg: 8 },
        color: "#fff",
        bgcolor: "#1e293b",
        "@media (max-height: 760px)": {
          px: { md: 4, lg: 7 },
          py: 4,
        },
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 960 540"
        width="100%"
        height="100%"
        preserveAspectRatio="xMidYMax slice"
        sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
      >
        <Box
          component="g"
          sx={{
            fill: "none",
            stroke: "#334155",
            strokeWidth: 100,
            opacity: 0.25,
          }}
        >
          <circle r="234" cx="196" cy="23" />
          <circle r="234" cx="790" cy="491" />
        </Box>
      </Box>

      <Box sx={{ position: "relative", zIndex: 1, maxWidth: 672 }}>
        <Typography
          component="h1"
          sx={{
            color: "#f1f5f9",
            fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
            fontSize: { md: 32, lg: 42, xl: 48 },
            fontWeight: 700,
            lineHeight: 1,
            "@media (max-height: 760px)": {
              fontSize: { md: 30, lg: 38 },
            },
          }}
        >
          {isMfa ? "Protegendo sua conta e sua operação" : "Lembre seus clientes pelo WhatsApp"}
        </Typography>
        <Typography
          sx={{
            mt: 3,
            color: "#94a3b8",
            fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
            fontSize: { md: 14, lg: 15, xl: 16 },
            lineHeight: 1.5,
            maxWidth: 672,
            "@media (max-height: 760px)": {
              mt: 2,
              fontSize: 14,
            },
          }}
        >
          Automatize lembretes, avisos e cobranças no momento certo, usando os
          dados do seu sistema para reduzir tarefas manuais e melhorar o
          acompanhamento dos seus clientes.
        </Typography>
        {!isMfa ? <AuthIntegrationStrip /> : null}
      </Box>
    </Box>
  );
}

function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [message, setMessage] = useState(normalizeLoginStateMessage(location.state?.message));
  const [step, setStep] = useState("login");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const usernameError = Boolean(message) && (!username || isInvalidCredentialsMessage(message));
  const passwordError = Boolean(message) && (!password || isInvalidCredentialsMessage(message));

  useEffect(() => {
    if (!isAuthenticated) {
      setCheckingSession(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      navigate("/painel");
    }, 1500);

    return () => window.clearTimeout(timeoutId);
  }, [isAuthenticated, navigate]);

  async function resolveUsername(login) {
    if (!login.includes("@")) return login;

    const response = await GetUsernameByEmail({ email: login });
    return response?.username || login;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!username || !password) {
      setMessage("Por favor, preencha todos os campos.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const cognitoUsername = await resolveUsername(username);
      const response = await signIn({ username: cognitoUsername, password });

      sessionStorage.setItem(
        METHOD_LOGIN_KEY,
        username.includes("@") ? "EMAIL" : "USERNAME",
      );

      if (response.isSignedIn) {
        window.location.href = "/";
        return;
      }

      if (response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        setUser(response);
        setStep("mfa");
        return;
      }

      if (response.nextStep?.signInStep === "RESET_PASSWORD") {
        await resetPassword({ username: cognitoUsername });
        navigate(`/reset-senha?username=${cognitoUsername}`);
        return;
      }

      sessionStorage.setItem(METHOD_LOGIN_KEY, "");
      setMessage(normalizeLoginError(response.message));
    } catch (error) {
      sessionStorage.setItem(METHOD_LOGIN_KEY, "");
      setMessage(normalizeLoginError(error.message));
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmMfa(event) {
    event?.preventDefault();

    if (!mfaCode) {
      notify("Error", "Preencha todos os campos", true);
      return;
    }

    setLoading(true);

    try {
      await confirmSignIn({
        challengeResponse: mfaCode,
        options: { user },
      });
      window.location.href = "/";
    } catch (error) {
      console.error("Erro no MFA:", error);
      notify("Error", "Ocorreu algum erro com a confirmação", true);
    } finally {
      setLoading(false);
    }
  }

  if (checkingSession) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isMfa = step === "mfa";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(430px, 1fr) minmax(420px, 1fr)",
        },
        minHeight: "100dvh",
        bgcolor: "#fff",
        width: "100%",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: { xs: "flex-start", sm: "center" },
          alignItems: "center",
          minHeight: "100dvh",
          width: "100%",
          minWidth: 0,
          px: { xs: 2, sm: 4, md: 5, lg: 7, xl: 8 },
          py: { xs: 3, sm: 4, md: 5, lg: 6 },
          overflowY: "auto",
          "@media (max-height: 760px)": {
            py: { xs: 2.5, sm: 3, md: 3 },
          },
          "@media (max-height: 680px)": {
            justifyContent: "flex-start",
            py: 2,
          },
          "@media (max-height: 560px)": {
            py: 1,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: {
              xs: "min(320px, calc(100vw - 32px))",
              sm: 320,
              lg: 330,
            },
            mb: { xs: 3, md: 4 },
            "@media (max-height: 760px)": {
              mb: 2.5,
            },
            "@media (max-height: 560px)": {
              mb: 1.25,
            },
          }}
        >
          <Box
            component="img"
            src={logoLogin123Zap}
            alt="123zap"
            sx={{
              display: "block",
              width: { xs: 68, md: 74 },
              height: "auto",
              "@media (max-height: 760px)": {
                width: 66,
              },
              "@media (max-height: 560px)": {
                width: 56,
              },
            }}
          />
        </Box>

        <Box
          component="form"
          onSubmit={isMfa ? handleConfirmMfa : handleSubmit}
          sx={{
            width: "100%",
            maxWidth: {
              xs: "min(320px, calc(100vw - 32px))",
              sm: 320,
              lg: 330,
            },
            minWidth: 0,
          }}
        >
          <Typography
            component="h2"
            sx={{
              color: "#141827",
              fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
              fontSize: { xs: 28, sm: 30, lg: 32 },
              fontWeight: 800,
              lineHeight: 1.25,
              "@media (max-height: 760px)": {
                fontSize: 28,
              },
              "@media (max-height: 560px)": {
                fontSize: 26,
              },
            }}
          >
            {isMfa ? "Digite o código enviado por e-mail" : "Bem-vindo!"}
          </Typography>

          <Typography
            sx={{
              mt: isMfa ? 1.5 : { xs: 3, md: 4 },
              mb: 2,
              color: "#667085",
              fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
              fontSize: { xs: 15, lg: 16 },
              lineHeight: 1.5,
              "@media (max-height: 760px)": {
                mt: isMfa ? 1 : 2.5,
                mb: 1.5,
                fontSize: 15,
              },
              "@media (max-height: 560px)": {
                mt: isMfa ? 0.75 : 1.25,
                mb: 1,
                fontSize: 14,
              },
            }}
          >
            {isMfa
              ? "Agora insira o código que enviamos ao seu e-mail para finalizar."
              : "Informe sua conta para entrar:"}
          </Typography>

          {isMfa ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <AuthTextField
                label="Código para autenticação"
                required
                name="mfacode"
                type="text"
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value)}
                autoFocus
                autoComplete="one-time-code"
                icon={<Pin />}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={primaryButtonSx}
              >
                Confirmar
              </Button>
            </Box>
          ) : (
            <>
              <AuthTextField
                label="Email"
                required
                name="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoFocus
                autoComplete="username"
                error={usernameError}
                helperText={usernameError ? getUsernameHelperText(message) : undefined}
                icon={<Mail />}
              />

              <AuthTextField
                label="Senha"
                required
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                error={passwordError}
                helperText={passwordError ? getPasswordHelperText(message) : undefined}
                icon={<Lock />}
                endAdornment={
                  <Box
                    component="button"
                    type="button"
                    aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    onClick={() => setShowPassword((current) => !current)}
                    sx={{
                      appearance: "none",
                      display: "grid",
                      placeItems: "center",
                      width: 24,
                      height: 24,
                      mr: 1.5,
                      p: 0,
                      border: 0,
                      borderRadius: 0,
                      outline: "none",
                      cursor: "pointer",
                      bgcolor: "transparent",
                      color: "#64748B",
                      "&:hover": {
                        bgcolor: "transparent",
                        color: "#334155",
                      },
                      "&:focus-visible": {
                        color: purple[600],
                      },
                      "& svg": {
                        width: 18,
                        height: 18,
                      },
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </Box>
                }
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mt: 1,
                  mb: 2,
                }}
              >
                <Typography
                  component="a"
                  href="/esqueci-senha"
                  sx={{
                    color: purple[600],
                    fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    textDecoration: "none",
                    whiteSpace: "nowrap",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Esqueci minha senha
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={primaryButtonSx}
              >
                Entrar
              </Button>

              <Typography
                sx={{
                  mt: { xs: 4, md: 5.5 },
                  color: "#1E293B",
                  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
                  fontSize: 14,
                  textAlign: "center",
                  "@media (max-height: 760px)": {
                    mt: 3.5,
                  },
                  "@media (max-height: 560px)": {
                    mt: 2,
                  },
                }}
              >
                É novo por aqui?
              </Typography>
              <Button
                type="button"
                variant="text"
                fullWidth
                onClick={() => {
                  window.location.href = signupUrl;
                }}
                sx={{
                  mt: 0.5,
                  p: 0,
                  color: purple[600],
                  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    bgcolor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Crie sua conta
              </Button>
            </>
          )}
        </Box>
      </Box>

      <BrandPanel isMfa={isMfa} />
    </Box>
  );
}

function isInvalidCredentialsMessage(message) {
  return [
    "Usuário ou senha inválidos.",
    "Credenciais inválidas. Por favor tente novamente.",
  ].includes(message);
}

function getUsernameHelperText(message) {
  if (message === "Por favor, preencha todos os campos.") {
    return "Informe seu e-mail ou usuário.";
  }

  return message;
}

function getPasswordHelperText(message) {
  if (message === "Por favor, preencha todos os campos.") {
    return "Informe sua senha.";
  }

  return message;
}

const primaryButtonSx = {
  minHeight: 40,
  px: 3,
  borderRadius: "999px",
  bgcolor: purple[600],
  boxShadow: "none",
  color: "#fff",
  fontFamily: "Inter, Roboto, Helvetica, Arial, sans-serif",
  fontWeight: 500,
  textTransform: "none",
  "&:hover": {
    bgcolor: purple[700],
    boxShadow: "none",
  },
  "&:active": {
    bgcolor: purple[800],
  },
  "&.Mui-disabled": {
    bgcolor: "rgba(79, 70, 229, 0.42)",
    color: "#fff",
  },
  "@media (max-height: 560px)": {
    minHeight: 38,
  },
};

export default LoginPage;
